import { inject, Injectable, signal, Type } from '@angular/core';
import { DialogMode, DialogType } from '@/models/dialog-mode.model';
import { BehaviorSubject, Subscription } from 'rxjs';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';

import { BinderService } from '../api/binder.service';
import { UserAccessService } from './user-access.service';
import { SignatureRequestComponent } from '@/landmark/binder/signatures/request/signature-request.component';
import { ApprovalRequestComponent } from '@/landmark/binder/components/request.component';
import { DocViewerComponent } from '@/landmark/binder/doc-viewer/doc-viewer.component';
import { ApprovalFormComponent } from '@/landmark/binder/components/binder.approval-form.component';
import { BinderEngineeringFeeComponent } from '@/landmark/binder/components/binder.engineering-fee.component';
import { BinderShareComponent } from '@/landmark/binder/components/binder.share.component';
import { AssemblyImportComponent } from '@/landmark/binder/components/assembly-import/assembly-import.component';
import { ProfileComponent } from '@/layout/components/profile/profile.component';
import { InviteReviewComponent } from '@/landmark/invite-review.component';
import { TaxErrorComponent } from '@/landmark/binder/components/tax-error.component';
import { PlanEditorComponent } from '@/landmark/operations/plan-management/plan-editor.component';
import { PlanBrowserComponent } from '@/landmark/plan-browser/plan-browser.component';
import { UserEditorComponent } from '@/landmark/operations/user-list/user-editor/user-editor.component';
import { ExRequestComponent } from '@/landmark/binder/components/exemption/request.component';
import { ExRespondComponent } from '@/landmark/binder/components/exemption/respond.component';
import { BinderSiteEditorComponent } from '@/landmark/binder/components/binder-site-editor.component';
import { OpsAssemblyEditorComponent } from '@/landmark/operations/assemblies/assemblies/assembly-editor/assembly-editor.component';
import { AssemblyPickerComponent } from '@/landmark/binder/components/assemblies/assembly-picker/assembly-picker.component';
import { BreakpointService } from '@/layout/service/breakpoint.service';

interface DialogConfig {
  header: string;
  width: string;
  height: string;
  position: string;
  closable: boolean;
  modal: boolean;
  closeOnEscape: boolean;
  contentStyle: {
    overflowY?: string;
    top: string;
    bottom: string;
  };
  data: {
    editAccess: boolean;
  };
  baseZIndex: number;
  maximizable?: boolean;
  breakpoints?: {
    [key: string]: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class BinderDialogService {
 
  dialogMode = signal<DialogMode>(new DialogMode('Drafting Details', false, 'drafting'));
  dialogRef: DynamicDialogRef | undefined;
  isDialogOpen = signal<boolean>(false);
  drawerOpen = signal<boolean>(false);
  drawerComponent = signal<Type<any> | null>(null);
  drawerHeader = signal<string>('');
  breakpointService = inject(BreakpointService);

  constructor(
    public dialogService: DialogService,
    private userAccessService: UserAccessService
  ) {
  }

  setDialogMode(mode: DialogMode) {
    this.dialogMode.set(mode);
  }

  toggleDrawer(type: DialogType, header: string, open: boolean) {
    this.drawerHeader.set(header);
    switch (type) {
      case 'drafting':
        this.drawerComponent.set(DocViewerComponent);
        break;
      // case 'generator':
      //   this.drawerComponent.set(GenerateComponent);
      //   break;
      case 'signatures':
        this.drawerComponent.set(SignatureRequestComponent);
        break;
      case 'design':
        this.drawerComponent.set(BinderSiteEditorComponent);
        break;
      case 'approval':
        this.drawerComponent.set(ApprovalRequestComponent);
        break;
      case 'docs':
        this.drawerComponent.set(DocViewerComponent);
        break;
      case 'respond':
        this.drawerComponent.set(ApprovalFormComponent);
        break;
      case 'exRequest':
        this.drawerComponent.set(ExRequestComponent);
        break;
      case 'exResponse':
        this.drawerComponent.set(ExRespondComponent);
        break;
      case 'assemblies':
        this.drawerComponent.set(AssemblyPickerComponent);
        break;
      case 'plans':
        this.drawerComponent.set(PlanBrowserComponent);
        break;
      case 'user':
        this.drawerComponent.set(UserEditorComponent);
        break;
      case 'engineering':
        this.drawerComponent.set(BinderEngineeringFeeComponent);
        break;
      case 'share':
        this.drawerComponent.set(BinderShareComponent);
        break;
      case 'invite':
        this.drawerComponent.set(InviteReviewComponent);
        break;
      case 'settings':
        this.drawerComponent.set(ProfileComponent);
        break;
      case 'assembly-import':
        this.drawerComponent.set(AssemblyImportComponent);
        break;
      case 'tax-error':
        this.drawerComponent.set(TaxErrorComponent);
        break;
      case 'plan-editor':
        this.drawerComponent.set(PlanEditorComponent);
        break;
      case 'assembly-editor':
        this.drawerComponent.set(OpsAssemblyEditorComponent);
        break;
      default:
        this.drawerComponent.set(DocViewerComponent);
        break;
    }
    this.drawerOpen.set(open);
  }

  toggleDialog(header: string, type: DialogType, show: boolean, position?: string, noWidth?: boolean) {
    if(!this.breakpointService.isBelowLg()) {
    const mode = {
      header: header,
      modal: true,

      show: show || false,
      type: type,
      position: position || 'center'
    };
    this.dialogMode.set(mode);

    let component: Type<any> = DocViewerComponent;
    let overflowY = 'hidden';

    let dialogConfig: DialogConfig = {
      header: header,
      width: 'auto',
      height: 'auto',
      maximizable: true,
      modal: true,
      closeOnEscape: true,
      closable: true,
      position: position || 'center',
      contentStyle: {
        overflowY: overflowY,
        top: '5px',
        bottom: '5px'
      },
      data: {
        editAccess: this.userAccessService.binderEditAccess()
      },
      baseZIndex: 10000
    }

    switch (type) {
      case 'drafting':
        component = DocViewerComponent;
        break;
      // case 'generator':
      //   component = GenerateComponent;
      //   break;
      case 'signatures':
        component = SignatureRequestComponent;
        break;
      case 'design':
        component = BinderSiteEditorComponent;
        dialogConfig.contentStyle.overflowY = 'scroll';
        break;
      case 'approval':
        component = ApprovalRequestComponent;
        break;
      case 'docs':
        component = DocViewerComponent;
        dialogConfig.maximizable = true;
        break;
      case 'respond':
        component = ApprovalFormComponent;
        break;
      case 'exRequest':
        component = ExRequestComponent;
        break;
      case 'exResponse':
        component = ExRespondComponent;
        break;
      case 'assemblies':
        component = AssemblyPickerComponent;
        dialogConfig.height = '80vh';
        break;
      case 'plans':
        component = PlanBrowserComponent;
        dialogConfig.contentStyle.overflowY = 'auto';
        break;
      case 'user':
        component = UserEditorComponent;
        dialogConfig.maximizable = false
        dialogConfig.contentStyle.overflowY = 'scroll';
        break;
      case 'engineering':
        component = BinderEngineeringFeeComponent;
        dialogConfig.maximizable = false;
        break;
      case 'share':
        component = BinderShareComponent;
        dialogConfig.maximizable = false;
        break;
      case 'invite':
        component = InviteReviewComponent;
        break;
      case 'settings':
        component = ProfileComponent;
        break;
      case 'assembly-import':
        component = AssemblyImportComponent;
        break;
      case 'tax-error':
        component = TaxErrorComponent;
        dialogConfig.maximizable = false;
        dialogConfig.closable = false;
        dialogConfig.closeOnEscape = false;
        break;
      case 'plan-editor':
        component = PlanEditorComponent;
        dialogConfig.contentStyle.overflowY = 'auto';
        break;
      case 'assembly-editor':
        component = OpsAssemblyEditorComponent;
        dialogConfig.contentStyle.overflowY = 'auto';
        dialogConfig.height = '70vh';
        break;
      default:
        component = DocViewerComponent;
        break;
    }

    console.log('dialog data', dialogConfig);
    if (!noWidth) {
      dialogConfig.breakpoints = {
        '1920px': '70vw',
        '1600px': '80vw',
        '1200px': '90vw',
        '960px': '100vw'
      };
      dialogConfig.width = '70%';
    }
    if (show) {
      this.dialogRef = this.dialogService.open(component, dialogConfig);
      this.dialogRef.maximize(true);

      this.isDialogOpen.set(true);
      if(this.dialogRef) {
        this.dialogRef.onClose.subscribe(() => {
          this.isDialogOpen.set(false);
        });
      }
    } else {
      this.closeDialog();
    }
  }
    else {
      this.toggleDrawer(type, header, show);
    }
  }

  closeDrawer() {
    this.drawerOpen.set(false);
    this.drawerComponent.set(null);
  }

  setDialogState(state: boolean) {
    this.isDialogOpen.set(state);
  }

  closeDialog(closeall?: boolean) {
    this.dialogRef?.close();
    // this.isDialogOpen.next(false);
  }
}